import {
  days,
  EventDetailsForView,
  EventPictureType,
  EventTimetableType,
  EventWithCustomField,
  getDayLabel,
  getDaysInRange,
  ParticipationRequestType,
} from "@ac-expo/api-types";
import {
  EventExceptionalClosingDateType,
  EventScheduleType,
  EventScheduleTypeWithClosedDates,
  formatTimetableHours,
  getAddressLabel,
  getGmapsLink,
  getName,
} from "@ac-expo/utils";
import { FileType } from "@prisma/client";
import { cn } from "@ui/utils";
import { format, isAfter, isBefore, isSameDay } from "date-fns";
import { fr } from "date-fns/locale";
import { useMemo } from "react";
import {
  ChatCircleIcon,
  EnvelopeIcon,
  FacebookLogoIcon,
  InstagramLogoIcon,
  LinkedinLogoIcon,
  LinkSimpleIcon,
  MapIcon,
  MoneyIcon,
  PhoneIcon,
  SunHorizonIcon,
  UserCircleIcon,
  XLogoIcon,
  YoutubeLogoIcon,
} from "../../../icons";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselSummary,
} from "../../Carousel";
import Thumbnail from "../../Thumbnail";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../Tooltip";
import ProtectedLink from "../ProtectedLink";

type InformationPanelDetailsProps = {
  as: "organiser" | "exhibitor" | "visitor";
  event: EventWithCustomField<EventDetailsForView>;
  participationRequestStatus: ParticipationRequestType["status"] | null;
  isTicketingVisible: boolean;
  onWebsiteClick?: () => void;
  onConversationClick?: () => void;
  onRequestParticipation?: () => void;
  actionGuard?: (action: () => void, fallback?: () => void, ignoreModal?: boolean) => void;
};

export function InformationPanelDetailsSection({
  event,
  onWebsiteClick,
  onConversationClick,
  actionGuard,
}: InformationPanelDetailsProps) {
  const { isIndoor, isOutdoor, isPaidEntry, socials, address } = event;

  function renderTags() {
    if (isIndoor && isOutdoor) {
      return "Intérieur & Extérieur";
    } else if (isIndoor) {
      return "Intérieur";
    } else if (isOutdoor) {
      return "Extérieur";
    }
  }

  const canSeePrivateInfos = useMemo(() => {
    let canSeePrivateInfos = false;
    actionGuard?.(() => (canSeePrivateInfos = true), undefined, true);
    return canSeePrivateInfos;
  }, [actionGuard]);

  const addressLabel = getAddressLabel(address);

  const organiser: Partial<typeof event.organiser.admin> =
    event.organiser.adminId !== "AC-EXPO" ? event.organiser.admin : {};
  const organiserName = event.organiserName ?? getName(organiser);
  const organiserEmail = event.organiserEmail ?? organiser.email;
  const organiserPhone = event.organiserPhone ?? organiser.phoneNumber;

  return (
    <div className="gap-4">
      <div className="flex items-center gap-3 pb-4">
        <SunHorizonIcon className="shrink-0" width={24} height={24} fill={"#F39200"} />
        <p className="text-base text-gray-600">{renderTags()}</p>
      </div>
      <div className="flex items-center gap-3 pb-4">
        <MoneyIcon className="shrink-0" width={24} height={24} fill={"#F39200"} />
        <p className="text-base text-gray-600">{isPaidEntry ? "Payant" : "Gratuit"}</p>
      </div>
      {socials.websiteUrl && (
        <div className="flex items-center gap-3 pb-4">
          <a
            href={socials.websiteUrl}
            className="flex gap-3 underline"
            onClick={onWebsiteClick}
            target="_blank"
            rel="noopener noreferrer"
          >
            <LinkSimpleIcon className="shrink-0" width={24} height={24} fill={"#F39200"} />
            <p className="text-base text-gray-600">{socials.websiteUrl}</p>
          </a>
        </div>
      )}
      {addressLabel && address && (
        <div className="pb-4">
          <a
            className="flex w-fit items-center gap-3 underline"
            href={getGmapsLink(address)}
            target="_blank"
            rel="noreferrer"
          >
            <MapIcon className="shrink-0" width={24} height={24} fill={"#F39200"} />
            <p className="text-base text-gray-600">{addressLabel}</p>
          </a>
        </div>
      )}
      <div className="flex items-center justify-between pb-4">
        {organiserName && (
          <div className="flex gap-3">
            <UserCircleIcon className="shrink-0" width={24} height={24} fill={"#F39200"} />
            <p className="text-base text-gray-600">{organiserName.toLocaleUpperCase()}</p>
          </div>
        )}
        <div className="flex gap-1">
          {organiserPhone && (
            <Tooltip>
              <TooltipContent>
                {canSeePrivateInfos ? organiserPhone : "Contacter l'organisateur par téléphone"}
              </TooltipContent>
              <TooltipTrigger>
                <ProtectedLink href={`tel:${organiserPhone}`} actionGuard={actionGuard}>
                  <div className="border-primary-400 rounded border px-2">
                    <PhoneIcon width={18} height={18} fill={"#287BB9"} />
                  </div>
                </ProtectedLink>
              </TooltipTrigger>
            </Tooltip>
          )}
          {organiserEmail && (
            <Tooltip>
              <TooltipContent>
                {canSeePrivateInfos ? organiserEmail : "Contacter l'organisateur par mail"}
              </TooltipContent>
              <TooltipTrigger>
                <ProtectedLink href={`mailto:${organiserEmail}`} actionGuard={actionGuard}>
                  <div className="border-primary-400 rounded border px-2">
                    <EnvelopeIcon width={18} height={18} fill={"#287BB9"} />
                  </div>
                </ProtectedLink>
              </TooltipTrigger>
            </Tooltip>
          )}
          {event.organiser.adminId !== "AC-EXPO" && (
            <Tooltip>
              <TooltipContent>Contacter l&apos;organisateur via la messagerie</TooltipContent>
              <TooltipTrigger>
                <ProtectedLink
                  href={`/conversations?participantId=${event.organiser.admin.id}`}
                  className={"flex grow"}
                  actionGuard={actionGuard}
                  onClick={onConversationClick}
                >
                  <div className="border-primary-400 rounded border px-2">
                    <ChatCircleIcon width={18} height={18} fill={"#287BB9"} />
                  </div>
                </ProtectedLink>
              </TooltipTrigger>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

type InformationPanelDescriptionProps = {
  event: EventWithCustomField<EventDetailsForView>;
};

export function InformationPanelDescriptionSection({ event }: InformationPanelDescriptionProps) {
  const { description } = event;

  return (
    <div className="py-6">
      <h4 className="font-normal">DESCRIPTION</h4>
      <p className="whitespace-pre-line pt-2 text-sm">{description || "Aucune description"}</p>
    </div>
  );
}

type InformationPanelEntrancesProps = {
  event: EventWithCustomField<EventDetailsForView>;
};

export function InformationPanelEntrancesSection({ event }: InformationPanelEntrancesProps) {
  const { eventEntrances } = event;
  if (!eventEntrances || eventEntrances.length === 0) return null;

  return (
    <div className="py-6">
      <h4 className="font-normal text-gray-700">ENTRÉES ET ADRESSES</h4>
      <div className="flex flex-col gap-4 pt-2 text-sm text-gray-600">
        {(eventEntrances || []).map((entrance, index) => (
          <a
            key={index}
            className="flex gap-1"
            href={getGmapsLink(entrance.address)}
            target="_blank"
            rel="noreferrer"
          >
            <MapIcon width={20} height={20} fill={""} />
            <p>{getAddressLabel(entrance.address)}</p>
          </a>
        ))}
      </div>
    </div>
  );
}

type InformationPanelScheduleProps = {
  event: EventWithCustomField<EventDetailsForView>;
};
export function InformationPanelScheduleSection({ event }: InformationPanelScheduleProps) {
  const { dates } = event;

  const getDateLabel = (date: Date) => {
    const dayName = getDayLabel(days[new Date(date).getDay()]);
    const dayDate = format(date, "d MMMM", { locale: fr });
    return `${dayName} ${dayDate}`;
  };

  const formatTime = (timeString: string) => {
    const [hour, minute] = timeString.split(":");
    return `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
  };

  const getTimetableLabel = (
    timetable: Omit<EventTimetableType, "id" | "createdAt" | "updatedAt">,
  ) => {
    if (timetable.state === "closed") {
      return "Fermé";
    }
    if (timetable.state === "continuouslyOpened") {
      return `${formatTime(timetable.openingTime ?? "00:00")} - ${formatTime(timetable.closingTime ?? "00:00")}`;
    }
    if (timetable.state === "opened") {
      return `${formatTime(timetable.openingTime ?? "00:00")} - ${formatTime(timetable.breakStartTime ?? "00:00")} et ${formatTime(timetable.breakEndTime ?? "00:00")} - ${formatTime(timetable.closingTime ?? "00:00")}`;
    }
    return "";
  };

  const getDiscontinuedTimetableLabels = (
    timetables: Omit<EventTimetableType, "id" | "createdAt" | "updatedAt">[],
  ) => {
    return timetables.map((timetable) => (
      <div key={timetable.day} className="flex flex-row gap-1">
        <p className="w-[150px] pl-4">{getDayLabel(timetable.day)}</p>
        <p>{formatTimetableHours(timetable)}</p>
      </div>
    ));
  };

  const getScheduleLabel = (schedule: EventScheduleType) => {
    if (isSameDay(schedule.fromDate, schedule.toDate))
      return <p className="font-bold">{getDateLabel(new Date(schedule.fromDate))}</p>;
    return (
      <div className="flex flex-row gap-1">
        <p>Du</p>
        <p className="font-bold">{getDateLabel(new Date(schedule.fromDate))}</p>
        <p>au</p>
        <p className="font-bold">{getDateLabel(new Date(schedule.toDate))}</p>
      </div>
    );
  };

  const getFullScheduleDisplay = (
    schedule: EventScheduleTypeWithClosedDates,
    isContinuous: boolean,
  ) => {
    const daysToDisplay = getDaysInRange(
      new Date(schedule.fromDate) ?? new Date(),
      new Date(schedule.toDate) ?? new Date(),
    );
    if (daysToDisplay.length === 0) return null;

    const timetablesToDisplay = schedule.timetables.filter((timetable) =>
      daysToDisplay.includes(timetable.day),
    );
    const basicTimeTable =
      schedule.timetables.find((timetable) => timetable.day === daysToDisplay[0]) ??
      timetablesToDisplay[0];

    return (
      <div
        className={cn("flex w-full flex-col gap-1 py-2", {
          "flex-row gap-8": isContinuous || daysToDisplay.length === 1,
        })}
      >
        <div>{getScheduleLabel(schedule)}</div>
        {(isContinuous || daysToDisplay.length === 1) && formatTimetableHours(basicTimeTable)}
        {!isContinuous &&
          daysToDisplay.length > 1 &&
          getDiscontinuedTimetableLabels(timetablesToDisplay)}
      </div>
    );
  };

  const getFullClosedDaysDisplay = (closedDays: EventExceptionalClosingDateType[]) => {
    if (closedDays.length < 1) return null;
    return (
      <div className="flex w-full flex-col gap-1 py-2">
        <p className="font-extrabold">Dates de fermeture exceptionnelles</p>
        {closedDays.map((closedDay) => (
          <p className="pl-4" key={closedDay.date}>
            {getDateLabel(new Date(closedDay.date))}
          </p>
        ))}
      </div>
    );
  };

  const exceptionalClosingDates = dates?.exceptionalClosingDates ?? [];
  const schedules = dates?.schedules ?? [];
  const orderedSchedules = schedules.sort((a, b) =>
    isBefore(a.fromDate ?? new Date(), b.fromDate ?? new Date()) ? -1 : 1,
  );
  const orderedExceptionalClosingDates = exceptionalClosingDates.sort((a, b) =>
    isBefore(a.date ?? new Date(), b.date ?? new Date()) ? -1 : 1,
  );
  const orderedSchedulesWithExceptionalClosingDates: EventScheduleTypeWithClosedDates[] =
    orderedSchedules.map((schedule) => {
      const relativeClosingDates = exceptionalClosingDates
        .filter(
          (date) => !isBefore(date.date, schedule.fromDate) && !isAfter(date.date, schedule.toDate),
        )
        .sort((a, b) => (isBefore(a.date ?? new Date(), b.date ?? new Date()) ? -1 : 1));
      return { ...schedule, closedDates: relativeClosingDates };
    });

  return (
    <div className="py-6">
      <h4 className="font-medium text-gray-700">DATES ET HORAIRES D&apos;OUVERTURE</h4>
      <ul className="flex h-fit flex-col gap-4 overflow-auto pt-2 text-sm">
        {orderedSchedulesWithExceptionalClosingDates.map((schedule, index) => (
          <li key={index} className="flex">
            {getFullScheduleDisplay(schedule, dates?.isContinuous ?? false)}
          </li>
        ))}
        <li key={"closed"} className="flex">
          {getFullClosedDaysDisplay(orderedExceptionalClosingDates)}
        </li>
      </ul>
    </div>
  );
}

type InformationPanelSocialProps = {
  event: EventWithCustomField<EventDetailsForView>;
  onSocialsClick?: () => void;
};

export function InformationPanelSocialSection({
  event,
  onSocialsClick,
}: InformationPanelSocialProps) {
  const { socials } = event;

  return (
    <div className="flex justify-center gap-6 pt-6">
      {socials.facebookUrl && (
        <a
          href={socials.facebookUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onSocialsClick}
        >
          <FacebookLogoIcon width={24} height={24} fill={"#1B6298"} />
        </a>
      )}
      {socials.twitterUrl && (
        <a
          href={socials.twitterUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onSocialsClick}
        >
          <XLogoIcon width={24} height={24} fill={"#1B6298"} />
        </a>
      )}
      {socials.instagramUrl && (
        <a
          href={socials.instagramUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onSocialsClick}
        >
          <InstagramLogoIcon width={24} height={24} fill={"#1B6298"} />
        </a>
      )}
      {socials.youtubeUrl && (
        <a
          href={socials.youtubeUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onSocialsClick}
        >
          <YoutubeLogoIcon width={24} height={24} fill={"#1B6298"} />
        </a>
      )}
      {socials.linkedinUrl && (
        <a
          href={socials.linkedinUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onSocialsClick}
        >
          <LinkedinLogoIcon width={24} height={24} fill={"#1B6298"} />
        </a>
      )}
    </div>
  );
}

type InformationPanelPicturesProps = {
  pictures: EventPictureType[];
  getFileUrl: (key: string, type: FileType) => string;
};

export function InformationPanelPicturesSection({
  pictures,
  getFileUrl,
}: InformationPanelPicturesProps) {
  return (
    <div className="flex flex-col">
      {pictures.length > 0 && (
        <Carousel>
          <CarouselContent>
            {pictures.map((picture, index) => {
              return (
                <CarouselItem key={index.toString()}>
                  <div className="flex size-full flex-col gap-2">
                    <div className="relative">
                      <Thumbnail
                        imageUrl={getFileUrl(picture.file.key, picture.file.type)}
                        objectFit="auto"
                      />
                      <CarouselSummary className="absolute bottom-2 z-20" />
                      <CarouselPrevious className="absolute left-4" />
                      <CarouselNext className="absolute right-4" />
                    </div>
                    <span>{picture.legend}</span>
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      )}
    </div>
  );
}
